<template>
  <div class="crypto-currency-select">
    <div class="crypto-currency-select__header">
      <ExchangeDropDownHeader @close="emit('close')">
        {{ i18n.t(`kano.crypto-currency-select.title`) }}
      </ExchangeDropDownHeader>
    </div>
    <div class="crypto-currency-select__search">
      <FilterInput v-model="searchCryptoCurrency" data-testid="crypto-currency-select-search" />
    </div>
    <div v-if="props.loading" class="crypto-currency-select__loading">
      <ExchangeLoading />
    </div>
    <PbScroll v-else>
      <ExchangeDropDownEmptyFilter v-if="isShowEmptyResult" />
      <div v-else class="crypto-currency-select__list">
        <CryptoCurrency
          v-for="(option, index) in items"
          :key="index"
          :selected="props.value.currency === option.currency"
          :currency="option.currency"
          :icon="option.icon"
          :label="option.name"
          @click.native="selectHandler(option)"
        />
      </div>
    </PbScroll>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';

import CryptoCurrency from '@kano/components/ExchangeFormV2/CryptoCurrency.vue';
import ExchangeLoading from '@/components/ExchangeFormV2/ExchangeLoading.vue';
import FilterInput from '@kano/components/ExchangeFormV2/FilterInput.vue';
import PbScroll from '@kano/components/ExchangeFormV2/PbScroll.vue';
import { i18n } from '@kano/i18n';
import type { CryptoOptionModel } from '@/models';
import { BaseOptionModel } from '@/models';

import ExchangeDropDownHeader from './ExchangeDropDownHeader.vue';
import ExchangeDropDownEmptyFilter from './ExchangeDropDownEmptyFilter.vue';
import { pushToDataLayer, DataLayerEvent } from '@paybis/frontend-common-lib/src/services/gtm';
import { Flow } from '@paybis/frontend-common-lib/src/types/exchangeForm/exchange-form';

const props = defineProps<{
  value: BaseOptionModel,
  flow: Flow
  items: CryptoOptionModel[]
  loading: boolean
}>();

const emit = defineEmits<{
  (event: 'close'): void,
  (event: 'input', option: CryptoOptionModel): void,
}>();

const searchCryptoCurrency = ref('');
onUnmounted(() => searchCryptoCurrency.value = '');

const items = computed(() => {
  if (searchCryptoCurrency.value.length) {
    const search = searchCryptoCurrency.value.toLowerCase();
    return props.items.filter(item => {
      return item.currency.toLowerCase().includes(search) 
      || item.name.toLowerCase().includes(search)
      || (i18n.t(`asset-names.${item.currency}`) as string).toLocaleLowerCase().includes(search);
    });
  }

  return props.items;
});

const selectHandler = (option: CryptoOptionModel) => {
  emit('input', option);
  pushToDataLayer({ event: DataLayerEvent.TO_TICKER_CHANGE });
  emit('close');
};

const isShowEmptyResult = computed(() => {
  return searchCryptoCurrency.value && items.value.length === 0;
});

onMounted(() => {
  pushToDataLayer({
    event: DataLayerEvent.TO_TICKER_VIEW,
  });
});
</script>

<style lang="scss" scoped>
@use '@kanoAssets/styles/breakpoints.scss';

.crypto-currency-select {
  display: flex;
  flex-direction: column;
  height: 100%;

  @include breakpoints.desktop {
    height: 512px;
  }

  &__list {
    margin: 0 14px;
    height: 100%;
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
